import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BusinessIcon from '@mui/icons-material/Business';

import useSession from '../auth/useSession';
import { usePosProfile } from './PosProfileContext';
import {
  fetchPosProfiles,
  getPosProfileData,
  checkOpeningEntry,
  createOpeningVoucher,
} from '../api/posProfile';

export default function PosProfileSelect() {
  const navigate = useNavigate();
  const { user } = useSession();
  const {
    setProfile,
    setProfileData,
    setOpeningEntry,
  } = usePosProfile();

  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [openingCash, setOpeningCash] = useState('0');
  const [error, setError] = useState(null);
  const [resuming, setResuming] = useState(false);
  const resumingRef = useRef(false);

  // Check if the user already has an open POS entry
  const openingEntryQuery = useQuery({
    queryKey: ['openingEntry', user],
    queryFn: () => checkOpeningEntry(user),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Fetch available POS Profiles
  const profilesQuery = useQuery({
    queryKey: ['posProfiles'],
    queryFn: fetchPosProfiles,
    staleTime: 60_000,
  });

  const resumeSession = useCallback(async (entry) => {
    // Guard: prevent double-execution (StrictMode, re-renders)
    if (resumingRef.current) return;
    resumingRef.current = true;
    setResuming(true);

    try {
      const data = await getPosProfileData(entry.pos_profile);
      setProfile({
        name: entry.pos_profile,
        company: entry.company,
      });
      setProfileData(data);
      setOpeningEntry(entry.name);
      navigate('/pos', { replace: true });
    } catch (err) {
      resumingRef.current = false;
      setResuming(false);
      setError(`Failed to resume session: ${err.message}`);
    }
  }, [navigate, setProfile, setProfileData, setOpeningEntry]);

  // If user already has an open entry, resume that session
  useEffect(() => {
    const openEntries = openingEntryQuery.data;
    if (openEntries && openEntries.length > 0 && !resumingRef.current) {
      resumeSession(openEntries[0]);
    }
  }, [openingEntryQuery.data, resumeSession]);

  // Mutation for creating an opening voucher
  const openShiftMutation = useMutation({
    mutationFn: async () => {
      const balanceDetails = [
        {
          mode_of_payment: 'Cash',
          opening_amount: parseFloat(openingCash) || 0,
        },
      ];
      return createOpeningVoucher(
        selectedProfile.name,
        selectedProfile.company,
        balanceDetails,
      );
    },
    onSuccess: async (result) => {
      try {
        const data = await getPosProfileData(selectedProfile.name);
        setProfile(selectedProfile);
        setProfileData(data);
        setOpeningEntry(result?.name || null);
        setOpenShiftDialog(false);
        navigate('/pos', { replace: true });
      } catch (err) {
        setError(`Failed to load profile data: ${err.message}`);
      }
    },
    onError: (err) => {
      setError(`Failed to open shift: ${err?.response?.data?._server_messages || err.message}`);
    },
  });

  function handleProfileSelect(profile) {
    setSelectedProfile(profile);
    setOpenShiftDialog(true);
  }

  const profiles = profilesQuery.data || [];
  const isLoading = profilesQuery.isLoading || openingEntryQuery.isLoading;

  // If resuming an existing open entry, show loading
  if (resuming) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography color="text.secondary">Resuming session...</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading POS profiles...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <StorefrontIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" gutterBottom>
          Select POS Counter
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose a POS profile to start your shift.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {profilesQuery.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load POS profiles. Please check your connection and try again.
        </Alert>
      )}

      {profiles.length === 0 && !profilesQuery.isError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No POS profiles are available for your account. Contact your administrator.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {profiles.map((p) => (
          <Card key={p.name} variant="outlined">
            <CardActionArea onClick={() => handleProfileSelect(p)}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {p.name}
                </Typography>
                <Stack spacing={1}>
                  {p.company && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {p.company}
                      </Typography>
                    </Stack>
                  )}
                  {p.warehouse && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarehouseIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {p.warehouse}
                      </Typography>
                    </Stack>
                  )}
                  {p.currency && (
                    <Chip label={p.currency} size="small" variant="outlined" />
                  )}
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Open Shift Dialog */}
      <Dialog
        open={openShiftDialog}
        onClose={() => !openShiftMutation.isLoading && setOpenShiftDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Open Shift</DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Opening shift for <strong>{selectedProfile.name}</strong>
            </Typography>
          )}
          <TextField
            autoFocus
            label="Opening Cash Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={openingCash}
            onChange={(e) => setOpeningCash(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenShiftDialog(false)}
            disabled={openShiftMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => openShiftMutation.mutate()}
            disabled={openShiftMutation.isLoading}
          >
            {openShiftMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Open Shift'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
